import { useNavigate, useLocation } from "react-router-dom";
import { useMemo } from "react";
import type { ProductType } from "@core/config/products.config";

/**
 * Hook pour gérer la navigation intelligente dans les pages légales
 * Permet un retour contextuel selon la page d'origine et le produit actuel
 */
export const useLegalNavigation = (productType: ProductType) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigation = useMemo(() => {
    // Détection de la page d'origine via document.referrer
    const referrer = document.referrer;
    const isFromSameProduct = referrer.includes(`/${productType}/`);
    const isFromPricing = referrer.includes("/pricing");
    const isFromDashboard = referrer.includes("/dashboard");

    const goBack = () => {
      // Si on vient du même produit, on utilise l'historique
      if (isFromSameProduct || window.history.length > 1) {
        navigate(-1);
      } else {
        // Sinon on retourne à la racine du produit (sauf date-polls qui utilise "/")
        if (productType === "date") {
          navigate("/");
        } else {
          navigate(`/${productType}`);
        }
      }
    };

    const goHome = () => {
      // Pour date-polls, le home est "/" (pas "/date")
      if (productType === "date") {
        navigate("/");
      } else {
        navigate(`/${productType}`);
      }
    };

    const goToDashboard = () => {
      // Pour date-polls, le dashboard est "/dashboard" (pas "/date/dashboard")
      if (productType === "date") {
        navigate("/dashboard");
      } else {
        navigate(`/${productType}/dashboard`);
      }
    };

    const goToPricing = () => {
      navigate("/pricing");
    };

    // Breadcrumbs pour la navigation hiérarchique
    const breadcrumbs = [
      {
        label: productType.charAt(0).toUpperCase() + productType.slice(1),
        href: productType === "date" ? "/" : `/${productType}`,
      },
      { label: "Documentation", href: `/${productType}/docs` },
      {
        label: getCurrentPageTitle(location.pathname),
        href: location.pathname,
      },
    ].filter(Boolean);

    return {
      goBack,
      goHome,
      goToDashboard,
      goToPricing,
      breadcrumbs,
      isFromSameProduct,
      isFromPricing,
      isFromDashboard,
    };
  }, [productType, navigate, location]);

  return navigation;
};

/**
 * Détermine le titre de la page actuelle selon le pathname
 */
function getCurrentPageTitle(pathname: string): string {
  // Détections spécifiques en premier (plus spécifique = plus prioritaire)
  if (pathname.includes("cg-u")) return "Conditions Générales d'Utilisation";
  if (pathname.includes("cg-v")) return "Conditions Générales de Vente";
  if (pathname.includes("privacy-ia")) return "Politique de Confidentialité IA";
  if (pathname.includes("privacy")) return "Confidentialité";
  if (pathname.includes("mentions-legales")) return "Mentions Légales";
  if (pathname.includes("terms")) return "Conditions Générales";
  if (pathname.includes("legal")) return "Mentions Légales";
  
  // Documentation générique en dernier (moins spécifique)
  if (pathname.includes("docs")) return "Documentation";
  
  return "Document";
}
